import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';
import { useHistory } from 'react-router-dom';
import './SearchBar.css';
import { highlightMatch } from "./searchUtils";
export function SearchBar({ data }) {
  const { t, language, translations } = useLanguage();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm.length > 3) {
      const results = searchTranslations(searchTerm, data);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, language, data]);

  const getPageFromKey = (key) => {
    // Extract the section from the new key format "flm.section.subsection#key"
    const match = key.match(/^flm\.([^.#]+)(?:\.([^#]+))?#/);
    if (!match) {
      return null;
    }

    const section = match[1];
    const subsection = match[2];

    // Map sections to their corresponding pages
    switch (section) {
      case "forward":
        if (subsection) {
          return { page: "forward", subPath: subsection };
        }
        return { page: "forward" };

      case "newMember":
        if (subsection) {
          const parts = subsection.split(".");
          // Handle nested subsections like 'gettingStarted.profile'
          if (parts.length > 1) {
            return {
              page: "new-members",
              subPath: parts[0],
              nestedPath: parts[1],
            };
          }
          return { page: "new-members", subPath: subsection };
        }
        return { page: "new-members" };

      case "resources":
        if (subsection) {
          return { page: "resources", subPath: subsection };
        }
        return { page: "resources" };

      case "nav":
      case "common":
        // Skip navigation and common translations
        return null;

      default:
        return null;
    }
  };

  const handleResultClick = (result) => {
    const basePath = `${process.env.PUBLIC_URL}/flm/${result.page.page}`;
    let fullPath = basePath;

    if (result.page.subPath) {
      fullPath += `/${result.page.subPath}`;
      if (result.page.nestedPath) {
        fullPath += `/${result.page.nestedPath}`;
      }
    }

    history.push(fullPath);
    setIsOpen(false);
    setSearchTerm("");
    setSearchResults([]);
  };

  const searchTranslations = (term, data) => {
    const currentTranslations = data ? data[language] : translations[language];
    const searchTermLower = term.toLowerCase();

    const matches = Object.entries(currentTranslations)
      .filter(([key, value]) => {
        // Skip navigation and common translations
        if (key.startsWith("flm.nav#") || key.startsWith("flm.common#")) {
          return false;
        }
        const valueLower = value.toLowerCase();
        return valueLower.includes(searchTermLower);
      })
      .map(([key, value]) => {
        return {
          key,
          value,
          index: value.toLowerCase().indexOf(searchTermLower),
          page: getPageFromKey(key),
        };
      })
      .filter((result) => result.page) // Only include results with valid pages
      .sort((a, b) => {
        // Prioritize exact matches
        const aExact = a.value.toLowerCase() === searchTermLower;
        const bExact = b.value.toLowerCase() === searchTermLower;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        // Then sort by match position and length
        if (a.index !== b.index) return a.index - b.index;
        return a.value.length - b.value.length;
      })
      .slice(0, 5);

    return matches;
  };

  return (
    <div className="search-container" ref={searchRef}>
      <button
        className="search-icon-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t("flm.common#search")}
      >
        <svg
          className="search-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </button>

      <div className={`search-popup ${isOpen ? "open" : ""}`}>
        <form onSubmit={(e) => e.preventDefault()}>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("flm.common#searchPlaceholder")}
            className="search-input"
          />
          {searchTerm && (
            <button
              type="button"
              className="clear-search"
              onClick={() => {
                setSearchTerm("");
                setSearchResults([]);
              }}
              aria-label={t("flm.common#clearSearch")}
            >
              ×
            </button>
          )}
        </form>
        {/* Search results display */}
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((result, index) => (
              <div
                key={result.key}
                className="search-result-item"
                onClick={() => handleResultClick(result)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleResultClick(result);
                  }
                }}
              >
                <div className="search-result-content">
                  {highlightMatch(result.value, searchTerm)}
                  <span className="search-result-page">
                    {result.page.subPath
                      ? `${result.page.page}/${result.page.subPath}`
                      : result.page.page}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 